import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Spinner,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Board20Regular,
  ChevronRight20Regular,
  ChevronDown20Regular,
  DataTrending24Regular,
  Wand20Regular,
} from '@fluentui/react-icons';
import { useDataModels } from '../contexts/DataModelsContext';
import { useTopBarTitle, useTopBarBreadcrumbs, useTopBarActions } from '../contexts/TopBarContext';
import { curateViews, type CuratedView } from '../components/business-views/viewCuration';
import { dataModelsService } from '../api/dataModelsService';
import type { DataModelView, DatasetViewColumnItem, ViewRelationshipItem } from '../api/dataModelsService';
import FieldsTable from '../components/business-views/FieldsTable';
import RelationshipsSection from '../components/business-views/ViewRelationshipTree';
import { MutationButton, useWorkspaceAccess } from '../components/security/WorkspaceAccess';
import { dataModelDisplayNames, resolveEntityDisplayName } from '../utils/dataModelDisplayNames';
import { formatTableName } from '../components/business-views/fieldUtils';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.overflow('auto'),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    ...shorthands.padding('14px', '32px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  headerInner: {
    maxWidth: '1400px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconTile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    flexShrink: 0,
    ...shorthands.borderRadius('8px'),
    background: `linear-gradient(135deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandBackgroundPressed} 100%)`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '24px',
  },
  description: {
    display: 'block',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '18px',
    maxWidth: '680px',
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  metaText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '16px',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    ...shorthands.padding('0', '32px', '24px'),
    maxWidth: '1400px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  section: {
    ...shorthands.padding('6px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    userSelect: 'none',
    ...shorthands.padding('4px', '0'),
  },
  collapsibleTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  collapsibleContent: {
    ...shorthands.padding('8px', '0', '0'),
  },
  previewScroll: {
    maxHeight: '350px',
    ...shorthands.overflow('auto'),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('20px', '0'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
});

// ─── Component ───────────────────────────────────────────────────────────────

const BusinessViewDetailPage: React.FC = () => {
  const { id, viewId } = useParams<{ id: string; viewId: string }>();
  const navigate = useNavigate();
  const { currentDataModel, loadDataModel } = useDataModels();
  const { runMutation } = useWorkspaceAccess();
  const styles = useStyles();

  const [loadFailed, setLoadFailed] = useState(false);
  const [columns, setColumns] = useState<DatasetViewColumnItem[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [relationshipsOpen, setRelationshipsOpen] = useState(false);
  const [viewLevelRelationships, setViewLevelRelationships] = useState<ViewRelationshipItem[]>([]);

  useEffect(() => {
    if (id) {
      setLoadFailed(false);
      loadDataModel(id).catch(() => setLoadFailed(true));
    }
  }, [id, loadDataModel]);

  useEffect(() => {
    if (!viewId) return;
    setColumnsLoading(true);
    dataModelsService.getViewColumns(viewId)
      .then(setColumns)
      .catch(() => setColumns([]))
      .finally(() => setColumnsLoading(false));

    dataModelsService.getViewRelationships(viewId)
      .then(setViewLevelRelationships)
      .catch(() => setViewLevelRelationships([]));
  }, [viewId]);

  const view: DataModelView | undefined = useMemo(
    () => currentDataModel?.views.find((v) => v.id === viewId),
    [currentDataModel, viewId]
  );

  const displayModel = useMemo(
    () => currentDataModel ? dataModelDisplayNames(currentDataModel) : undefined,
    [currentDataModel]
  );
  const curatedView: CuratedView | undefined = useMemo(() => {
    const displayView = displayModel?.views.find((v) => v.id === viewId);
    return displayView ? curateViews([displayView])[0] : undefined;
  }, [displayModel, viewId]);
  const entityDisplayName = useCallback(
    (name: string) => resolveEntityDisplayName(name, displayModel?.labels) ?? formatTableName(name),
    [displayModel]
  );

  useTopBarTitle(curatedView?.displayName ?? 'View');
  useTopBarBreadcrumbs([
    { text: 'Data Models', to: '/data-models' },
    { text: currentDataModel?.name ?? '...', to: `/data-models/${id}` },
    { text: curatedView?.displayName ?? 'View' },
  ]);

  const handleCreateDashboard = useCallback(() =>
    runMutation(() => {
      const name = curatedView?.displayName || view?.name || '';
      navigate(`/dashboards?viewId=${viewId}&viewName=${encodeURIComponent(name)}`);
    }), [navigate, runMutation, viewId, curatedView, view]);

  const handleOpenStudio = useCallback(() => {
    navigate(`/data-models/${id}/studio?viewId=${viewId}`);
  }, [navigate, id, viewId]);

  useTopBarActions(
    useMemo(() => (
      <>
        <Button appearance="secondary" icon={<Wand20Regular />} onClick={handleOpenStudio}>
          Semantic Studio
        </Button>
        <MutationButton
          appearance="primary"
          icon={<Board20Regular />}
          onClick={handleCreateDashboard}
        >
          Create Dashboard
        </MutationButton>
      </>
    ), [handleOpenStudio, handleCreateDashboard])
  );

  // Derived data
  const calculatedCount = useMemo(() => columns.filter((c) => c.is_calculated).length, [columns]);
  const tableCount = useMemo(() => view?.baseTables?.length ?? 0, [view]);

  const isCrossDomain = useMemo(() => {
    if (!view) return false;
    const vt = view.viewType || '';
    return vt === 'multi_fact' || (view.baseTables && view.baseTables.length > 3);
  }, [view]);

  const datasetRelationships = useMemo(() => {
    if (!currentDataModel?.relationships || !view?.baseTables) return [];
    const tables = new Set(view.baseTables.map((t) => t.toLowerCase()));
    return currentDataModel.relationships.filter(
      (r) =>
        tables.has(r.fromDataset.name.toLowerCase()) ||
        tables.has(r.toDataset.name.toLowerCase())
    );
  }, [currentDataModel, view]);

  // Loading / error states
  if (!currentDataModel || !view) {
    if (loadFailed) {
      return <div className={styles.loadingContainer}><Text>View not found.</Text></div>;
    }
    return <div className={styles.loadingContainer}><Spinner size="medium" /></div>;
  }

  const viewName = curatedView?.displayName || view.name;

  return (
    <div className={styles.page}>
      {/* ─── Header ─── */}
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.titleRow}>
            <div className={styles.titleLeft}>
              <div className={styles.iconTile}><DataTrending24Regular /></div>
              <Text className={styles.title}>{viewName}</Text>
            </div>
          </div>
          {curatedView?.shortDescription && (
            <Text className={styles.description}>{curatedView.shortDescription}</Text>
          )}
          <div className={styles.chipRow}>
            {curatedView && curatedView.keyMetrics.length > 0 && curatedView.keyMetrics.map((m, i) => (
              <Badge key={i} appearance="outline" size="medium">{m}</Badge>
            ))}
            <Text className={styles.metaText}>
              {columns.length} fields • {calculatedCount} calculated • {tableCount} tables
              {isCrossDomain ? ' • Cross-domain' : ''}
            </Text>
          </div>
        </div>
      </div>

      {/* ─── Sections ─── */}
      <div className={styles.sections}>
        {/* Overview + Common Questions */}
        <div className={styles.section} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground2, lineHeight: '20px' }}>
            <span style={{ fontWeight: 600, color: tokens.colorNeutralForeground3, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '6px' }}>Overview:</span>
            {view.descriptionJson?.what_it_shows || view.descriptionJson?.summary || view.description || 'No description available.'}
          </Text>
          {curatedView && curatedView.exampleQuestions.length > 0 && (
            <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, lineHeight: '20px' }}>
              <span style={{ fontWeight: 600, color: tokens.colorNeutralForeground3, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '6px' }}>Questions:</span>
              {curatedView.exampleQuestions.join(' | ')}
            </Text>
          )}
        </div>

        {/* Fields Table */}
        <div className={styles.section} style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <FieldsTable columns={columns} loading={columnsLoading} entityDisplayName={entityDisplayName} />
        </div>

        {/* Preview (collapsible) */}
        <div className={styles.section}>
          <div className={styles.collapsibleHeader} onClick={() => setPreviewOpen((v) => !v)}>
            {previewOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
            <Text className={styles.collapsibleTitle}>Preview Data</Text>
          </div>
          {previewOpen && (
            <div className={`${styles.collapsibleContent} ${styles.previewScroll}`}>
              <div className={styles.emptyState}>
                <Text>Preview data is not available yet.</Text>
                <MutationButton
                  appearance="subtle"
                  size="small"
                  icon={<Board20Regular />}
                  onClick={handleCreateDashboard}
                >
                  Create Dashboard to explore
                </MutationButton>
              </div>
            </div>
          )}
        </div>

        {/* Relationships (collapsible) */}
        <div className={styles.section} style={{ borderBottom: 'none' }}>
          <div className={styles.collapsibleHeader} onClick={() => setRelationshipsOpen((v) => !v)}>
            {relationshipsOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
            <Text className={styles.collapsibleTitle}>Relationships</Text>
          </div>
          {relationshipsOpen && (
            <div className={styles.collapsibleContent}>
              <RelationshipsSection
                viewLevelRelationships={viewLevelRelationships}
                datasetRelationships={datasetRelationships}
                baseTables={view.baseTables || []}
                isCrossDomain={isCrossDomain}
                viewName={viewName}
                entityDisplayName={entityDisplayName}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessViewDetailPage;